import { X } from 'lucide-react'
import { Dialog as RadixDialog } from 'radix-ui'
import { useState, type ReactNode } from 'react'

import { cn } from '../cn'
import { Button, IconButton, type ButtonVariant } from './button'
import { Input } from './input'

export interface DialogProps {
  readonly open: boolean
  readonly onOpenChange: (open: boolean) => void
  readonly title: ReactNode
  readonly description?: ReactNode
  readonly children?: ReactNode
  readonly footer?: ReactNode
  readonly size?: 'sm' | 'md' | 'lg'
  readonly className?: string
}

const SIZE = { sm: 'max-w-[420px]', md: 'max-w-[560px]', lg: 'max-w-[760px]' } as const

/** Centred modal. Focus is trapped and restored by Radix; Escape and overlay click close it. */
export function Dialog({
  open,
  onOpenChange,
  title,
  description,
  children,
  footer,
  size = 'md',
  className,
}: DialogProps) {
  return (
    <RadixDialog.Root open={open} onOpenChange={onOpenChange}>
      <RadixDialog.Portal>
        <RadixDialog.Overlay className="fixed inset-0 z-50 bg-ink/50 backdrop-blur-[2px] data-[state=open]:animate-[fade-in_.15s_ease]" />
        <RadixDialog.Content
          className={cn(
            'fixed top-1/2 left-1/2 z-50 flex max-h-[calc(100vh-32px)] w-[calc(100vw-32px)] -translate-x-1/2 -translate-y-1/2 flex-col rounded-lg border border-line bg-panel shadow-lg outline-none data-[state=open]:animate-[fade-in_.18s_ease]',
            SIZE[size],
            className,
          )}
        >
          <div className="flex items-start justify-between gap-4 border-b border-line px-5 py-4">
            <div className="min-w-0">
              <RadixDialog.Title className="font-display text-[18px] font-semibold">
                {title}
              </RadixDialog.Title>
              {description !== undefined ? (
                <RadixDialog.Description className="mt-1 text-[13px] text-muted">
                  {description}
                </RadixDialog.Description>
              ) : (
                <RadixDialog.Description className="sr-only">{title}</RadixDialog.Description>
              )}
            </div>
            <RadixDialog.Close asChild>
              <IconButton label="Close" variant="ghost" size="sm">
                <X />
              </IconButton>
            </RadixDialog.Close>
          </div>
          {children !== undefined && (
            <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">{children}</div>
          )}
          {footer !== undefined && (
            <div className="flex flex-wrap items-center justify-end gap-2 border-t border-line px-5 py-3">
              {footer}
            </div>
          )}
        </RadixDialog.Content>
      </RadixDialog.Portal>
    </RadixDialog.Root>
  )
}

export interface ConfirmDialogProps {
  readonly open: boolean
  readonly onOpenChange: (open: boolean) => void
  readonly title: ReactNode
  readonly description?: ReactNode
  readonly confirmLabel?: string
  readonly cancelLabel?: string
  readonly confirmVariant?: Extract<ButtonVariant, 'primary' | 'danger'>
  readonly loading?: boolean
  readonly onConfirm: () => void
  /** Destructive actions: the user must type this exact text to enable the confirm button. */
  readonly typeToConfirm?: string
  readonly children?: ReactNode
}

export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  confirmVariant = 'primary',
  loading = false,
  onConfirm,
  typeToConfirm,
  children,
}: ConfirmDialogProps) {
  const [typed, setTyped] = useState('')
  const blocked = typeToConfirm !== undefined && typed.trim() !== typeToConfirm

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) setTyped('')
        onOpenChange(next)
      }}
      title={title}
      description={description}
      size="sm"
      footer={
        <>
          <Button
            variant="ghost"
            onClick={() => {
              onOpenChange(false)
            }}
            disabled={loading}
          >
            {cancelLabel}
          </Button>
          <Button variant={confirmVariant} onClick={onConfirm} loading={loading} disabled={blocked}>
            {confirmLabel}
          </Button>
        </>
      }
    >
      {children}
      {typeToConfirm !== undefined && (
        <label className="mt-2 block text-[12.5px] font-semibold text-ink-2">
          Type <span className="font-mono font-medium text-lost">{typeToConfirm}</span> to confirm
          <Input
            className="mt-1.5"
            value={typed}
            onChange={(event) => {
              setTyped(event.target.value)
            }}
            autoComplete="off"
          />
        </label>
      )}
    </Dialog>
  )
}
